import React, { useMemo, useState } from 'react'
import { App, Card, Steps } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Company } from '../../../types/company/Company'
import { createSourceMap } from '../service/sourceMapService'
import { useSourceMapSuggestions } from '../queries/useSourceMapSuggestions'
import sourceMapQueryKeys from '../queries/sourceMapQueryKeys'
import { buildMappingMatrix } from '../utils/buildMappingMatrix'
import { groupSuggestionsBySibling } from '../utils/groupSuggestionsBySibling'
import { excludeAlreadyMapped } from '../utils/excludeAlreadyMapped'
import {
  RowAction,
  RowResolution,
  SiblingDecision,
  buildSiblingDecision,
  defaultRowAction
} from '../utils/buildSiblingDecision'
import { isCellIncomplete } from '../utils/mappingLevel'
import { collectSourceMapPayloads } from '../utils/collectSourceMapPayloads'
import { SourceMap } from '../types'
import { MAPPING_MATRIX_ROUTE } from '../mappingRoutes'
import MappingExitLink from './MappingExitLink'
import WizardStepCompany from './wizard/WizardStepCompany'
import WizardStepSuggestions from './wizard/WizardStepSuggestions'
import WizardStepReview from './wizard/WizardStepReview'
import WizardStepConfirm from './wizard/WizardStepConfirm'

interface OnboardingWizardProps {
  companies: Company[]
  sourceMaps: SourceMap[]
  groupCompanyId?: string
  loading?: boolean
}

const WIZARD_STEPS = [
  { key: 'company', label: 'Yhtiö' },
  { key: 'suggestions', label: 'Ehdotukset' },
  { key: 'uncertain', label: 'Epävarmat' },
  { key: 'confirm', label: 'Vahvista' }
] as const

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ companies, sourceMaps, groupCompanyId, loading }) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1)
  const [targetId, setTargetId] = useState('')
  const [selectedSiblingIds, setSelectedSiblingIds] = useState<Set<string>>(new Set())
  const [rowActionOverrides, setRowActionOverrides] = useState<Record<string, RowAction>>({})
  const [resolutions, setResolutions] = useState<Record<string, RowResolution>>({})
  const [reviewIndex, setReviewIndex] = useState(0)
  const [committing, setCommitting] = useState(false)

  const target = companies.find(company => company.id === targetId)
  const selectedSiblings = companies.filter(company => selectedSiblingIds.has(company.id))

  const defaultSiblingsFor = (newTargetId: string): Set<string> => {
    const { rows } = buildMappingMatrix(companies, sourceMaps)
    const row = rows.find(r => r.company.id === newTargetId)
    if (!row) return new Set()
    return new Set(row.cells.filter(isCellIncomplete).map(cell => cell.counterpartyId))
  }

  const handleSelectTarget = (id: string) => {
    setTargetId(id)
    setSelectedSiblingIds(defaultSiblingsFor(id))
    setRowActionOverrides({})
    setResolutions({})
  }

  const handleToggleSibling = (id: string) => {
    setSelectedSiblingIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const {
    data: forwardSuggestions,
    isLoading: isForwardLoading,
    isError: isForwardError
  } = useSourceMapSuggestions(step >= 2 && targetId ? [targetId] : [])

  const candidatesBySibling = useMemo(() => {
    const grouped = groupSuggestionsBySibling(forwardSuggestions, Array.from(selectedSiblingIds))
    return excludeAlreadyMapped(grouped, targetId, sourceMaps)
  }, [forwardSuggestions, selectedSiblingIds, targetId, sourceMaps])

  const rowActions = useMemo(() => {
    const map: Record<string, RowAction> = {}
    selectedSiblings.forEach(sibling => {
      map[sibling.id] =
        rowActionOverrides[sibling.id] ??
        defaultRowAction(candidatesBySibling.get(sibling.id) ?? { customer: [], supplier: [] })
    })
    return map
  }, [selectedSiblings, rowActionOverrides, candidatesBySibling])

  const handleRowActionChange = (siblingId: string, action: RowAction) => {
    setRowActionOverrides(prev => ({ ...prev, [siblingId]: action }))
  }

  const reviewSiblings = selectedSiblings.filter(sibling => rowActions[sibling.id] === 'review')

  const handleSearch = () => {
    setRowActionOverrides({})
    setResolutions({})
    setStep(2)
  }

  const handleContinueFromSuggestions = () => {
    setReviewIndex(0)
    setStep(reviewSiblings.length > 0 ? 3 : 4)
  }

  const handleResolve = (siblingId: string, resolution: RowResolution) => {
    setResolutions(prev => ({ ...prev, [siblingId]: resolution }))
  }

  const handleSkipBoth = (siblingId: string) => {
    setResolutions(prev => ({ ...prev, [siblingId]: { customer: [], supplier: [] } }))
  }

  const handleReviewNext = () => {
    if (reviewIndex + 1 < reviewSiblings.length) setReviewIndex(reviewIndex + 1)
    else setStep(4)
  }

  const handleReviewPrev = () => {
    if (reviewIndex > 0) setReviewIndex(reviewIndex - 1)
    else setStep(2)
  }

  const decisions = useMemo(() => {
    const map = new Map<string, SiblingDecision>()
    selectedSiblings.forEach(sibling => {
      const candidates = candidatesBySibling.get(sibling.id) ?? { customer: [], supplier: [] }
      map.set(sibling.id, buildSiblingDecision(rowActions[sibling.id], resolutions[sibling.id], candidates))
    })
    return map
  }, [selectedSiblings, rowActions, resolutions, candidatesBySibling])

  const handleCommit = async () => {
    if (!target) return

    const payloads = collectSourceMapPayloads(target, selectedSiblings, decisions)
    if (payloads.length === 0) return

    setCommitting(true)
    const results = await Promise.allSettled(payloads.map(createSourceMap))
    setCommitting(false)
    queryClient.invalidateQueries({ queryKey: sourceMapQueryKeys.all })

    const succeeded = results.filter(result => result.status === 'fulfilled').length
    const failed = results.length - succeeded
    if (failed === 0) {
      message.success(
        t('groupCustomerMapping:wizardCommitSuccess', '{{count}} linkitystä tallennettu', { count: succeeded })
      )
      navigate(MAPPING_MATRIX_ROUTE)
    } else {
      message.warning(
        t('groupCustomerMapping:wizardCommitPartial', '{{succeeded}} onnistui, {{failed}} epäonnistui', {
          succeeded,
          failed
        })
      )
    }
  }

  return (
    <div style={{ maxWidth: 1000 }}>
      <MappingExitLink />
      <Card
        loading={loading}
        title={
          <div style={{ display: 'flex', alignContent: 'center', height: '100%' }}>
            <Steps
              size="small"
              current={step - 1}
              items={WIZARD_STEPS.map(({ key, label }) => ({
                title: t(`groupCustomerMapping:wizardStep-${key}`, label)
              }))}
            />
          </div>
        }
      >
        {step === 1 && (
          <WizardStepCompany
            companies={companies}
            groupCompanyId={groupCompanyId}
            targetId={targetId}
            selectedSiblingIds={selectedSiblingIds}
            onSelectTarget={handleSelectTarget}
            onToggleSibling={handleToggleSibling}
            onCancel={() => navigate(MAPPING_MATRIX_ROUTE)}
            onSearch={handleSearch}
          />
        )}

        {step === 2 && target && (
          <WizardStepSuggestions
            target={target}
            siblings={selectedSiblings}
            candidatesBySibling={candidatesBySibling}
            rowActions={rowActions}
            onRowActionChange={handleRowActionChange}
            loading={isForwardLoading}
            error={isForwardError}
            onBack={() => setStep(1)}
            onContinue={handleContinueFromSuggestions}
          />
        )}

        {step === 3 && target && reviewSiblings[reviewIndex] && (
          <WizardStepReview
            target={target}
            reviewSiblings={reviewSiblings}
            index={reviewIndex}
            candidatesBySibling={candidatesBySibling}
            resolutions={resolutions}
            onResolve={handleResolve}
            onSkipBoth={handleSkipBoth}
            onPrev={handleReviewPrev}
            onNext={handleReviewNext}
          />
        )}

        {step === 4 && target && (
          <WizardStepConfirm
            target={target}
            selectedSiblings={selectedSiblings}
            candidatesBySibling={candidatesBySibling}
            decisions={decisions}
            committing={committing}
            onBack={() => setStep(reviewSiblings.length > 0 ? 3 : 2)}
            onCommit={handleCommit}
          />
        )}
      </Card>
    </div>
  )
}

export default OnboardingWizard
